// frontend/src/hooks/useRoleCapabilities.js
//
// Loads the CALLING USER's own effective role capabilities once and exposes
// { loading, hasCapability }. Backed by GET /api/v1/roles/me
// (worker/lib/roles.js) — deliberately the "any authenticated user" self-
// scoped endpoint, not GET /api/v1/roles (superuser-only, the admin Role
// Management screen's own data source).
//
// Added 2026-09-26 to close a real gap: roleModules.js's MODULE_ROLES nav-
// visibility list was a static array with zero awareness of a tenant's
// per-role capability overrides (Role Management), so a role granted a NEW
// capability (e.g. frontdesk gaining SPA_GYM_MANAGEMENT) still couldn't see
// the corresponding nav item even though the backend already allowed the
// underlying API calls. This hook is what canSeeModule()'s 4th optional
// param now consults.
//
// Mirrors useEntitlements.js's exact shape (module-scope cache + in-flight
// dedup so multiple consumers share one fetch, "default to visible while
// loading" tie-break) — same idiom, different endpoint.
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

let _cache = null; // { capabilities: Map<string, boolean>, label, title } once loaded
let _inflight = null;

async function loadOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await apiFetch('/roles/me');
      const data = await res.json().catch(() => ({}));
      const capabilities = new Map(
        (data.success ? data.capabilities || [] : []).map((c) => [c.capability, !!c.enabled]),
      );
      // label = this person's role label (tenant-customised); title = their own
      // job title if the owner gave them one (migrations/0097) — display only.
      _cache = { capabilities, label: data.success ? data.label || null : null, title: data.success ? data.title || null : null };
    } catch {
      _cache = { capabilities: new Map(), label: null, title: null };
    } finally {
      _inflight = null;
    }
    return _cache;
  })();
  return _inflight;
}

export function clearRoleCapabilitiesCache() {
  _cache = null;
  _inflight = null;
}

export function useRoleCapabilities() {
  const [state, setState] = useState(_cache || { capabilities: null });
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    if (_cache) {
      setState(_cache);
      setLoading(false);
      return undefined;
    }
    loadOnce().then((c) => {
      if (alive) {
        setState(c);
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const hasCapability = (capabilityKey) => {
    const c = state.capabilities;
    // While loading (null), default to NOT granting an override — the
    // static MODULE_ROLES array (already checked first by canSeeModule)
    // still applies, so this never HIDES a module the role already has by
    // default; it just means a newly-granted override might not show up
    // for one render until this loads, which is the safer default here
    // (unlike useEntitlements' "default visible," a capability override is
    // additive access, not a UX convenience — no harm in a brief delay).
    if (!c) return false;
    return !!c.get(capabilityKey);
  };

  // can(capability, role, builtInRoles) — "may this person do X?" for a
  // page-level control. The server's answer (role default + this tenant's
  // role override + THIS USER's own override, resolved in /roles/me) is
  // authoritative once loaded, so a right granted to one user — or taken
  // away from a role — is honoured. Before it loads, or if it could not be
  // loaded (empty map), fall back to the built-in role list the page used
  // to hard-code, so a superuser is never locked out of their own screen by
  // a failed request. Added 2026-10-09: Room Management, Room Status,
  // Portion Stock, the Settings sub-sections etc. each hard-coded a role
  // array, so granting front-desk Ben "Room Management" unlocked the API but
  // the page still said "Access denied".
  const can = (capabilityKey, role, builtInRoles = []) => {
    const c = state.capabilities;
    if (c && c.has(capabilityKey)) return !!c.get(capabilityKey);
    return builtInRoles.includes(role);
  };

  return { loading, hasCapability, can, title: state.title || null, label: state.label || null };
}
